import { cn, getQualityGrade, qualityGradeConfig, trustLevelConfig, calculatePBNScore, calculateLinkHealth } from '../lib/utils';

/**
 * Format a number compactly: 1234 → "1.2K", 1234567 → "1.2M"
 */
function formatCompact(n) {
  if (n == null) return null;
  const num = parseInt(n);
  if (isNaN(num)) return null;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toString();
}

/**
 * HealthPill - Compact quality grade pill with rich tooltip.
 *
 * Shows: [Strong ●] green, [Moderate ●] yellow, [Weak ▲] orange, [Toxic ✕] red
 * Sub-line on md+: DFS backlinks count and referring domains
 * Tooltip: Quality Score, Trust Level, Speed/Failures/Stability, PBN Signals
 */
export default function HealthPill({
  qualityScore,
  da,
  pa,
  spamScore,
  traffic,
  referringDomains,
  referringIps,
  backlinksCount,
  dfsSpamScore,
  dfsRank,
  dfsBacklinksCount,
  dfsReferringDomains,
  consecutiveFailures = 0,
  lastStatusChange,
  lastResponseMs,
}) {
  // Compute grade from stored quality_score, or recalculate if missing
  let score = qualityScore != null ? parseFloat(qualityScore) : null;
  let grade = getQualityGrade(score);
  let partial = false;

  // Use explicit DFS props or fall back to the legacy referringDomains/backlinksCount props
  const effectiveBacklinksCount = dfsBacklinksCount ?? backlinksCount;
  const effectiveReferringDomains = dfsReferringDomains ?? referringDomains;

  if (grade == null) {
    // Recalculate client-side if no stored score
    const computed = calculateLinkHealth({
      da: parseFloat(da) || 0,
      pa: parseFloat(pa) || 0,
      spamScore: parseFloat(spamScore) || 0,
      traffic: parseInt(traffic) || 0,
      referringDomains: parseInt(referringDomains) || 0,
      referringIps: parseInt(referringIps) || 0,
      backlinksCount: parseInt(backlinksCount) || 0,
      dfsSpamScore: dfsSpamScore != null ? parseFloat(dfsSpamScore) : null,
      dfsRank: dfsRank != null ? parseInt(dfsRank) : null,
      consecutiveFailures: consecutiveFailures || 0,
    });
    score = computed.score;
    grade = computed.grade;
    partial = computed.partial;
  } else {
    // Check if DFS data is absent for stored scores too
    partial = dfsRank == null && (!referringDomains || parseInt(referringDomains) === 0);
  }

  const gradeConfig = qualityGradeConfig[grade] || qualityGradeConfig.toxic;

  // PBN / Trust classification
  const { pbnScore, trustLevel, signals } = calculatePBNScore({
    da: parseFloat(da) || 0,
    traffic: parseInt(traffic) || 0,
    referringDomains: parseInt(referringDomains) || 0,
    referringIps: parseInt(referringIps) || 0,
    backlinksCount: parseInt(backlinksCount) || 0,
    spamScore: parseFloat(spamScore) || 0,
    dfsSpamScore: dfsSpamScore != null ? parseFloat(dfsSpamScore) : null,
  });
  const trustConfig = trustLevelConfig[trustLevel];

  // Days since last status change
  const daysSinceChange = lastStatusChange
    ? Math.floor((Date.now() - new Date(lastStatusChange).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  // Grade icon
  const gradeIcon = grade === 'strong' ? '●' : grade === 'moderate' ? '●' : grade === 'weak' ? '▲' : '✕';

  // Display label with partial indicator
  const displayLabel = partial ? `${gradeConfig.label}*` : gradeConfig.label;

  // Compact DFS counts
  const blCompact = formatCompact(effectiveBacklinksCount);
  const refsCompact = formatCompact(effectiveReferringDomains);
  const hasDfsSubline = blCompact != null || refsCompact != null;

  // Build tooltip with factor breakdown
  const tooltipLines = [
    `Quality: ${score != null ? (score * 100).toFixed(0) : '?'} (${gradeConfig.label})`,
    `Trust: ${trustConfig.label}`,
  ];
  if (hasDfsSubline) {
    tooltipLines.push(`DFS: ${blCompact ?? '-'} backlinks · ${refsCompact ?? '-'} ref. domains`);
  }
  tooltipLines.push(
    `Speed: ${lastResponseMs != null ? `${lastResponseMs}ms` : 'N/A'} | Failures: ${consecutiveFailures} | Stable: ${daysSinceChange != null ? `${daysSinceChange}d` : 'N/A'}`
  );
  if (pbnScore >= 2) {
    tooltipLines.push(`PBN penalty: -${Math.round((1 - [1.0, 1.0, 0.90, 0.80, 0.65, 0.50, 0.40, 0.30][Math.min(pbnScore, 7)]) * 100)}%`);
  }
  if (partial) {
    tooltipLines.push('* Based on Moz data only. Run DFS refresh for full analysis.');
  }
  if (signals.length > 0) {
    tooltipLines.push(`PBN Signals: ${signals.join(', ')}`);
  } else {
    tooltipLines.push('PBN Signals: None');
  }

  return (
    <div
      className="inline-flex flex-col items-center cursor-default"
      title={tooltipLines.join('\n')}
    >
      <div
        className={cn(
          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap',
          gradeConfig.bg,
          gradeConfig.color,
        )}
      >
        <span>{displayLabel}</span>
        <span className="text-[10px]">{gradeIcon}</span>
      </div>
      {hasDfsSubline && (
        <div className="hidden md:flex items-center gap-1 text-[10px] text-muted-foreground tabular-nums mt-0.5">
          {blCompact != null && <span>{blCompact} BL</span>}
          {blCompact != null && refsCompact != null && <span>·</span>}
          {refsCompact != null && <span>{refsCompact} refs</span>}
        </div>
      )}
    </div>
  );
}
